import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ReactNode, useState } from 'react'
import { Separator } from '@/components/ui/separator'
import { FormStateContext } from 'app/frontend/base/config/context/forms'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Card from './Card'

interface Props {
  className?: string
  sections: {
    [key: string]: React.FC
  }
  defaultSection?: string
  entityType: string
  titleAction?: ReactNode
}

const EntitySettings = ({
  className,
  entityType,
  defaultSection,
  sections,
  titleAction,
}: Props) => {
  const { t } = useTranslation()
  const { hash: initialSection } = useLocation()

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [activeSection, setActiveSection] = useState(
    initialSection
      ? initialSection.replace('#', '')
      : defaultSection ?? entityType,
  )
  const [pendingSection, setPendingSection] = useState<string>()

  const handleSectionChange = (section: string) => {
    setActiveSection(section)
    setPendingSection(undefined)
    setHasUnsavedChanges(false)
    history.replaceState({}, '', `#${section}`)
  }

  const widthClass =
    activeSection === 'premium' ||
    (activeSection === 'notifications' && entityType === 'team')
      ? 'max-w-6xl'
      : 'max-w-4xl'

  return (
    <div>
      <h2 className={`flex items-center justify-between ${widthClass}`}>
        <span>Settings</span>
        {titleAction}
      </h2>

      <ConfirmDialog
        open={!!pendingSection}
        descriptionKey={'forms.unsaved-changes-warning'}
        onCancel={() => setPendingSection(undefined)}
        onContinue={() => {
          handleSectionChange(pendingSection || 'team')
        }}
      />

      <Card className={`pt-3 px-4 pb-6 ${widthClass} ${className ?? ''}`}>
        <Tabs
          value={activeSection}
          onValueChange={(value) => {
            if (hasUnsavedChanges) {
              setPendingSection(value)
            } else {
              handleSectionChange(value)
            }
          }}
        >
          <TabsList className='flex-wrap'>
            {Object.keys(sections).map((section) => (
              <TabsTrigger key={section} value={section}>
                <div>{t(`settings.sections.${section}`)}</div>
              </TabsTrigger>
            ))}
          </TabsList>

          <Separator className='mt-2 mb-6' />

          {Object.entries(sections).map(([section, Component]) => (
            <TabsContent key={section} value={section} id={section}>
              <FormStateContext.Provider
                value={{
                  hasUnsavedChanges,
                  setHasUnsavedChanges,
                  handleSectionChange,
                }}
              >
                <Component />
              </FormStateContext.Provider>
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </div>
  )
}

export default EntitySettings
